<!-- src/views/admin/AdminCompletedFormsListView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- PREMIUM HEADER -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl mb-1 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight">
              {{ formTitle }} Records
            </h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
              Submission Management • Sentinel Safe Premium
            </p>
          </div>
          <button @click="$router.go(-1)" class="px-6 py-3 bg-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center no-print border border-white/10">
            <ArrowUturnLeftIcon class="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <!-- SEARCH, SORT, & FILTER BAR -->
        <div class="bg-white p-6 rounded-b-[40px] shadow-xl border-x border-b border-slate-200 mb-10 flex flex-col lg:flex-row gap-6 items-center no-print animate-fadeIn">
          <!-- Search Input -->
          <div class="relative w-full lg:w-96 group">
            <span class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-[#20FCF1]">
              <MagnifyingGlassIcon class="h-5 w-5 text-slate-400 group-focus-within:text-[#20FCF1]" />
            </span>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search Reference or Name..." 
              class="form-input !pl-12"
            />
          </div>

          <!-- Filter by Status -->
          <div class="w-full lg:w-48">
            <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-2">Status Filter</label>
            <select v-model="statusFilter" class="form-input !py-2 !font-bold">
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Requires Action">Requires Action</option>
            </select>
          </div>

          <!-- Sort Order -->
          <div class="w-full lg:w-48">
            <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-2">Timeline Sort</label>
            <select v-model="sortOrder" class="form-input !py-2 !font-bold">
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          <!-- Results Count -->
          <div class="lg:ml-auto flex flex-col items-end">
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Entry Count</span>
            <span class="text-lg font-black text-[#0A1630]">{{ filteredSubmissions.length }}</span>
          </div>
        </div>

        <!-- LOADING & ERROR STATES -->
        <div v-if="loading" class="text-center py-20 bg-white rounded-[40px] shadow-sm border border-slate-200">
          <div class="animate-spin rounded-full h-12 w-12 border-b-4 border-[#20FCF1] mx-auto mb-4"></div>
          <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">Synchronizing Recordset...</p>
        </div>
        
        <div v-else-if="error" class="text-center py-12 bg-red-50 rounded-[40px] border border-red-100 px-6">
          <div class="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon class="h-8 w-8 text-red-600" />
          </div>
          <h2 class="text-xl font-black text-red-900 uppercase tracking-tight">Access Error</h2>
          <p class="text-red-700 text-sm font-bold mt-2">{{ error }}</p>
        </div>

        <div v-else-if="filteredSubmissions.length === 0" class="text-center py-20 bg-white rounded-[40px] shadow-sm border-2 border-dashed border-slate-100">
          <p class="text-sm font-bold text-slate-400 uppercase tracking-widest">No matching records identified.</p>
          <button @click="resetFilters" class="mt-4 text-[#20FCF1] font-black uppercase text-[10px] tracking-widest hover:underline">Clear Search Criteria</button>
        </div>

        <!-- SUBMISSIONS TABLE -->
        <div v-else class="bg-white shadow-2xl rounded-[40px] border border-slate-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-slate-200">
              <thead class="bg-[#0A1630]">
                <tr>
                  <th scope="col" class="px-6 py-5 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">QR Verification</th>
                  <th scope="col" class="px-6 py-5 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Form Reference</th>
                  <th scope="col" class="px-6 py-5 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Assigned User</th>
                  <th scope="col" class="px-6 py-5 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Submission Date</th>
                  <th scope="col" class="px-6 py-5 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Status</th>
                  <th scope="col" class="px-6 py-5 text-right text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-slate-100">
                <tr v-for="submission in filteredSubmissions" :key="submission.id" class="hover:bg-slate-50 transition-colors duration-150">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div v-if="submission.qrCodeUrl" class="bg-white p-1 rounded-xl border border-slate-100 inline-block shadow-sm">
                      <img :src="submission.qrCodeUrl" alt="QR" class="w-14 h-14 object-contain">
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-black text-[#0A1630] tracking-wider">
                    {{ submission.humanReadableId || submission.referenceNumber || submission.id }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-black text-[#0A1630] uppercase">{{ submission.userName || 'Guest' }}</div>
                    <div class="text-[10px] font-bold text-slate-400 lowercase">{{ submission.userEmail || 'N/A' }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-xs font-bold text-slate-500 uppercase">
                    {{ submission.submittedAt ? formatDate(submission.submittedAt) : 'N/A' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="statusClass(submission.status)"
                      class="px-4 py-1.5 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm"
                    >
                      {{ submission.status || 'PENDING' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div class="flex justify-end gap-2">
                      <button @click="viewRecord(submission.id)" class="px-4 py-2 bg-slate-100 text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] transition-all">
                        View
                      </button>
                      <button v-if="submission.pdfGenerated" @click="viewPdf(submission.pdfStoragePath)" class="px-4 py-2 bg-slate-900 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">
                        PDF
                      </button>
                      <button v-else @click="generatePdf(submission.id)" :disabled="submission.isGenerating" class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl font-black uppercase text-[10px] tracking-widest hover:brightness-110 disabled:opacity-50 border border-[#20FCF1]/20">
                        {{ submission.isGenerating ? 'Wait...' : 'Generate' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db, storage as fbStorage, auth } from '@/firebaseConfig'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import QRCode from 'qrcode';
import { useUserStore } from '@/stores/userStore';
import { 
  MagnifyingGlassIcon, 
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const submissions = ref([]);
const loading = ref(true);
const error = ref(null);
const formIdentifier = ref(route.params.formIdentifier);

const searchQuery = ref('');
const statusFilter = ref('');
const sortOrder = ref('desc');

const functions = getFunctions(auth.app, 'europe-west2');

// FIXED: Added subContractorPack to the map
const formIdentifierToCollectionMap = {
  yourDetails: 'onboardingFormSubmissions',
  employeeInduction: 'onboardingFormSubmissions',
  rolesAndResponsibilities: 'onboardingFormSubmissions',
  literacyTest: 'onboardingFormSubmissions',
  primarySponsorContract: 'onboardingFormSubmissions',
  annualHealthAssessment: 'onboardingFormSubmissions',
  eyesightDeclaration: 'onboardingFormSubmissions',
  rightToWork: 'onboardingFormSubmissions',
  ppeRequest: 'onboardingFormSubmissions',
  employeeHandbookSignOff: 'onboardingFormSubmissions',
  subContractorPack: 'onboardingFormSubmissions',
  observationForm: 'healthsafetyFormSubmissions',
  accidentForm: 'healthsafetyFormSubmissions',
  accidentWitnessStatementForm: 'healthsafetyFormSubmissions',
  worksiteInspectionForm: 'healthsafetyFormSubmissions',
  cossCommunicationsMonitoring: 'healthsafetyFormSubmissions',
  fatigueManagementPlan: 'fatigueFormSubmissions',
  fatigueAssessment: 'fatigueFormSubmissions',
  fatigueRiskAssessment: 'fatigueFormSubmissions',
};

const formCategoryToPermissionMap = {
  onboardingFormSubmissions: 'canViewOnboarding',
  healthsafetyFormSubmissions: 'canViewHealthAndSafety',
  fatigueFormSubmissions: 'canViewFatigue',
};

const formTitle = computed(() => {
  const titles = {
    yourDetails: 'Your Details',
    employeeInduction: 'Company Induction',
    rolesAndResponsibilities: 'Roles & Responsibilities',
    literacyTest: 'Literacy Test',
    primarySponsorContract: 'Primary Sponsor Contract',
    annualHealthAssessment: 'Annual Health Assessment',
    eyesightDeclaration: 'Eyesight Declaration',
    rightToWork: 'Right To Work',
    ppeRequest: 'PPE Request',
    employeeHandbookSignOff: 'Company Handbook Sign-Off',
    subContractorPack: 'Sub-Contractor Pack',
    observationForm: 'Observation Form',
    accidentForm: 'Accident Report',
    accidentWitnessStatementForm: 'Accident Witness Statement',
    worksiteInspectionForm: 'Worksite Inspection',
    cossCommunicationsMonitoring: 'COSS Communications Monitoring',
    fatigueManagementPlan: 'Fatigue Management Plan',
    fatigueAssessment: 'Fatigue Assessment',
    fatigueRiskAssessment: 'Fatigue Risk Assessment',
  };
  return titles[formIdentifier.value] || formIdentifier.value;
});

const filteredSubmissions = computed(() => {
  let result = [...submissions.value];
  if (statusFilter.value) result = result.filter(s => s.status === statusFilter.value);
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase().trim();
    result = result.filter(s => {
      const ref = (s.humanReadableId || s.referenceNumber || '').toLowerCase();
      const name = (s.userName || '').toLowerCase();
      return ref.includes(q) || name.includes(q);
    });
  }
  result.sort((a, b) => {
    const dateA = a.submittedAt?.seconds || 0;
    const dateB = b.submittedAt?.seconds || 0;
    return sortOrder.value === 'desc' ? dateB - dateA : dateA - dateB;
  });
  return result;
});

const resetFilters = () => { searchQuery.value = ''; statusFilter.value = ''; sortOrder.value = 'desc'; };

const fetchSubmissions = async () => {
  loading.value = true;
  error.value = null;
  try {
    const adminClientId = userStore.claims.clientId;
    const collectionName = formIdentifierToCollectionMap[formIdentifier.value];
    if (!collectionName || !adminClientId) throw new Error("Initialization mapping data missing.");

    const requiredPermission = formCategoryToPermissionMap[collectionName];
    if (!userStore.isAdmin && (!requiredPermission || !userStore.permissions[requiredPermission])) {
        throw new Error("Permission denied for this record type.");
    }

    const q = query(collection(db, collectionName), where('clientId', '==', adminClientId), where('formIdentifier', '==', formIdentifier.value));
    const querySnapshot = await getDocs(q);

    const submissionPromises = querySnapshot.docs.map(async (submissionDoc) => {
      const data = submissionDoc.data();
      let resolvedUserName = data.userName || 'Guest';
      let resolvedUserEmail = data.userEmail || 'N/A';
      const submitterId = data.userId || data.submittedBy;
      if (submitterId) {
        const userProfileSnap = await getDoc(doc(db, 'userProfiles', submitterId));
        if (userProfileSnap.exists()) {
          const up = userProfileSnap.data();
          resolvedUserName = `${up.firstName || ''} ${up.lastName || ''}`.trim();
          resolvedUserEmail = up.email || 'No Email';
        }
      }
      const completedFormUrl = `${window.location.origin}/completed-form/${collectionName}/${submissionDoc.id}`;
      let qrUrl = null;
      try { qrUrl = await QRCode.toDataURL(completedFormUrl, { width: 200, margin: 1 }); } catch (e) { console.error(e); }
      return { id: submissionDoc.id, ...data, userName: resolvedUserName, userEmail: resolvedUserEmail, qrCodeUrl: qrUrl, isGenerating: false };
    });

    submissions.value = await Promise.all(submissionPromises);
  } catch (err) { error.value = err.message; }
  finally { loading.value = false; }
};

const statusClass = (status) => {
  const s = (status || 'PENDING').toUpperCase();
  if (s === 'COMPLETED') return 'bg-[#EBFDF8] text-[#0A1630] border border-[#20FCF1]/30';
  if (s === 'REQUIRES ACTION') return 'bg-red-50 text-red-700 border border-red-200';
  return 'bg-amber-50 text-amber-700 border border-amber-200';
};

const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const viewRecord = (id) => {
  router.push({ name: 'completedForm', params: { collectionName: formIdentifierToCollectionMap[formIdentifier.value], submissionId: id } });
};

const generatePdf = async (id) => {
  const sub = submissions.value.find(s => s.id === id);
  if (!sub || sub.isGenerating) return;
  sub.isGenerating = true;
  try {
    const res = await httpsCallable(functions, 'generatePdf')({ submissionId: id, collectionName: formIdentifierToCollectionMap[formIdentifier.value], formIdentifier: formIdentifier.value });
    if (res.data.success && res.data.downloadUrl) window.open(res.data.downloadUrl, '_blank');
  } catch (err) { alert(err.message); } finally { sub.isGenerating = false; }
};

const viewPdf = async (path) => {
  try {
    const url = await getDownloadURL(storageRef(fbStorage, path));
    window.open(url, '_blank');
  } catch (e) { alert("PDF Error."); }
};

onMounted(() => { if (userStore.isAuthReady) fetchSubmissions(); });
watch(() => userStore.isAuthReady, (ready) => { if (ready) fetchSubmissions(); });
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm; 
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>